import { Dispatch, Fragment, SetStateAction } from 'react';

import dynamic from 'next/dynamic';
import Image from 'next/image';

import clsx from 'clsx';

import { useGlobal } from '@hultafors/hultaforsgroup/hooks';
import { MenuItemFragment } from '@hultafors/hultaforsgroup/types';
import { useMarketPicker } from '@hultafors/shared/context/hooks';

import LogoSvg from '../../svg/logo.svg';
import PartnerSvg from '../../svg/partner.svg';
import { Button } from '../button/button';
import { DesktopMenu } from '../desktop-menu/desktop-menu';
import { NavigationOpenState } from '../nav/nav';
import LanguageSvg from './distribution-global.svg';
import styles from './navigation-desktop.module.scss';
import {
  IconLabel,
  Logo,
  NavigationDesktopWrapper,
  PartnerLink,
  TogglerButton,
  TopActions,
  TopMenu,
} from './navigation-desktop.styled';

const Link = dynamic(() =>
  import('@hultafors/shared/components').then((module) => module.SharedLink),
);

interface NavigationDesktopProps {
  defaultOpenSettings: NavigationOpenState;
  isOpen: NavigationOpenState;
  setIsOpen: Dispatch<SetStateAction<NavigationOpenState>>;
  toggleMarketPicker(): void;
}

export const NavigationDesktop: React.FC<NavigationDesktopProps> = ({
  defaultOpenSettings,
  isOpen,
  setIsOpen,
  toggleMarketPicker,
}) => {
  const { globalContent: global, menu } = useGlobal();
  const { activeMarket } = useMarketPicker();
  const datoLogo = global?.navigationLogo?.url;

  const topMenuMapper = (item: MenuItemFragment) => {
    const toggleDesktopMenu = () => {
      const type = item.title;

      if (type === isOpen.desktop) {
        setIsOpen({
          ...defaultOpenSettings,
          desktop: '',
          desktopMenu: false,
        });
      } else {
        setIsOpen({
          ...defaultOpenSettings,
          desktop: type || '',
          desktopMenu: type ? true : false,
        });
      }
    };

    if (!item.submenu) {
      return (
        <Link
          adjustStyles={{ color: 'var(--color-white)' }}
          className={styles['link']}
          href={item.url || ''}
          key={`MenuItemLink-${item.id}`}
          onClick={toggleDesktopMenu}
          styleType="sub-heading"
        >
          {item.title}
        </Link>
      );
    }

    if (item?.title) {
      return (
        <Fragment key={`MenuItem-${item.id}`}>
          <Button
            className={clsx(
              styles['button'],
              isOpen.desktop === item.title && styles['button--active'],
            )}
            data-slidein-ignore="desktopMenu"
            label={item.title}
            onClick={toggleDesktopMenu}
            textColor="var(--color-white)"
            withoutStyles
          />
          <DesktopMenu
            menuItems={item.submenu.menuItems}
            menuType={item.title || ''}
            openType={isOpen.desktop}
            partnerPortalUrl="/partner-portal"
            toggleDesktopMenu={toggleDesktopMenu}
          />
        </Fragment>
      );
    }

    return null;
  };

  function renderLanguageIcon() {
    let iconUrl;

    if (!activeMarket || (!activeMarket?.flag && !activeMarket?.symbol?.url)) {
      return (
        <LanguageSvg
          aria-hidden={true}
          focusable={false}
          height={24}
          width={24}
        />
      );
    }
    if (activeMarket?.flag && !activeMarket?.symbol?.url) {
      iconUrl = `https://flagcdn.com/${activeMarket.flag}.svg`;
    }
    if (activeMarket?.symbol?.url) {
      iconUrl = activeMarket.symbol.url;
    }
    return (
      <img
        alt={activeMarket.name || 'Market Icon'}
        height={24}
        src={iconUrl ?? ''}
        width={24}
      />
    );
  }

  return (
    <NavigationDesktopWrapper
      className={clsx('DesktopNav', styles['desktop-nav'])}
      data-test="NavigationDesktop"
    >
      <Logo>
        <Link
          aria-label="Hultafors Group"
          href="/"
          isWrapper
          noHoverEffect
          onClick={() => setIsOpen({ ...defaultOpenSettings })}
        >
          {datoLogo ? (
            <Image
              alt="Hultafors Group"
              fill
              priority
              src={datoLogo}
              style={{ objectFit: 'contain' }}
            />
          ) : (
            <LogoSvg aria-hidden={true} focusable={false} height={14} />
          )}
        </Link>
      </Logo>
      <TopMenu>{menu.menuItems.map(topMenuMapper)}</TopMenu>
      <TopActions>
        <TogglerButton
          aria-label={global?.changeLanguage || ''}
          onClick={toggleMarketPicker}
        >
          {renderLanguageIcon()}
          <IconLabel>{global?.changeLanguage}</IconLabel>
        </TogglerButton>
        {global.partnerPortalUrl && (
          <PartnerLink href={global.partnerPortalUrl}>
            <PartnerSvg
              aria-hidden={true}
              color="white"
              focusable={false}
              height={24}
              width={24}
            />
            <IconLabel>{global?.partnerPortalLabel}</IconLabel>
          </PartnerLink>
        )}
      </TopActions>
    </NavigationDesktopWrapper>
  );
};
